import { useState } from 'react';
import { AppleOutlined, DeleteOutlined, DesktopOutlined, LaptopOutlined, WindowsOutlined } from '@ant-design/icons';
import { App, Segmented } from 'antd';
import { api } from '../api';
import { ActiveTag, DataTable, ErrorNotice, PageHeader, Panel, RowActions } from '../components';
import { countLabel, formatDate, mobileLabel, timeAgo, useLoad } from '../lib';
function platformIcon(platform) {
    const name = platform.toLowerCase();
    if (name.includes('mac') || name.includes('darwin'))
        return <AppleOutlined />;
    if (name.includes('win'))
        return <WindowsOutlined />;
    if (name.includes('linux'))
        return <DesktopOutlined />;
    return <LaptopOutlined />;
}
export function DevicesPage() {
    const { message } = App.useApp();
    const [filter, setFilter] = useState('active');
    const { data, error, loading, reload } = useLoad(async () => {
        const [devices, accounts] = await Promise.all([api('/v1/admin/devices'), api('/v1/admin/accounts')]);
        return { devices: devices.devices, mobiles: new Map(accounts.accounts.map((account) => [account.id, mobileLabel(account.mobile)])) };
    });
    const devices = data?.devices ?? [];
    const shown = devices.filter((row) => (filter === 'all' ? true : filter === 'active' ? !row.removedAt : Boolean(row.removedAt)));
    const activeCount = devices.filter((row) => !row.removedAt).length;
    async function remove(row) {
        try {
            await api(`/v1/admin/devices/${row.id}/remove`, { method: 'POST' });
            message.success(`${row.name} is signed out`);
            await reload();
        }
        catch (err) {
            message.error(err instanceof Error ? err.message : 'Could not sign the computer out');
        }
    }
    return (<>
      <PageHeader title="Devices" description="Computers signed in to a Kivonex account with its mobile number. Removing one signs it out and frees a place in the plan's computer limit."/>
      <ErrorNotice error={error}/>
      <Panel flush>
        <div className="toolbar">
          <Segmented value={filter} onChange={setFilter} options={[
            { value: 'active', label: countLabel('Signed in', activeCount, data !== null) },
            { value: 'removed', label: countLabel('Signed out', devices.length - activeCount, data !== null) },
            { value: 'all', label: countLabel('All', devices.length, data !== null) }
        ]}/>
        </div>
        <DataTable rowKey="id" loading={loading} dataSource={shown} pagination={shown.length > 20 ? { pageSize: 20, showSizeChanger: false } : false} scroll={{ x: 860 }} locale={{ emptyText: devices.length ? 'No computers match.' : 'Computers appear here once a customer signs in on the desktop app.' }} columns={[
            {
                title: 'Computer',
                skeleton: 'person',
                render: (_value, row) => (<div className="person">
                  <span className="stat-icon tone-slate" style={{ flex: 'none' }}>{platformIcon(row.platform)}</span>
                  <div className="person-text">
                    <strong>{row.name}</strong>
                    <span>
                      {row.platform} · app {row.appVersion}
                    </span>
                  </div>
                </div>)
            },
            {
                title: 'Account',
                skeleton: 'lines',
                render: (_value, row) => (<span>
                  {row.accountName}
                  <span className="cell-sub">{data?.mobiles.get(row.accountId) || row.accountEmail || '—'}</span>
                </span>)
            },
            { title: 'Signed in', dataIndex: 'activatedAt', width: 140, render: (value) => formatDate(value) },
            { title: 'Last seen', dataIndex: 'lastSeenAt', width: 140, render: (value) => timeAgo(value) },
            { title: 'Status', dataIndex: 'removedAt', width: 120, skeleton: 'tag', render: (value) => <ActiveTag active={!value} on="Signed in" off="Signed out"/> },
            {
                title: '',
                width: 56,
                align: 'right',
                skeleton: 'actions',
                render: (_value, row) => (<RowActions label={`Actions for ${row.name}`} actions={[
                        !row.removedAt && {
                            key: 'remove',
                            icon: <DeleteOutlined />,
                            label: 'Remove (sign out)',
                            danger: true,
                            hint: 'Frees a place in the computer limit',
                            confirm: {
                                title: 'Sign this computer out?',
                                description: `${row.name} is signed out of ${row.accountName}'s account. To use it again, the customer signs in on it with their mobile number and a code. Company data on the computer is kept.`,
                                okText: 'Sign out'
                            },
                            onClick: () => remove(row)
                        }
                    ]}/>)
            }
        ]}/>
      </Panel>
    </>);
}
